import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Briefcase,
  CaretDown,
  CheckCircle,
  Gear,
  GraduationCap,
  Keyboard,
  List,
  MagnifyingGlass,
  Moon,
  SignOut,
  Sun,
  User,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function AppHeader() {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    theme,
    setTheme,
    preferredLanguage,
    handleLanguageChange,
    currentUser,
    currentOrg,
    currentOrgId,
    organizations,
    handleSwitchOrganization,
    notifications,
    profileDialogOpen,
    setProfileDialogOpen,
    handleLogout,
    isMobile,
  } = useApp();

  const [searchOpen, setSearchOpen] = useState(false);
  const unreadNotifications = (notifications || []).filter((n) => !n.read).length;

  // Placeholder for missing actions in context
  const handleRestartOnboarding = () => {
    toast.info("Onboarding tour feature coming soon!");
  };

  const handleKeyboardShortcuts = () => {
    toast.info("Keyboard shortcuts feature coming soon!");
  };

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 h-16 flex items-center justify-between px-6 transition-all duration-200">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="lg:hidden text-muted-foreground hover:text-foreground"
          aria-label="Toggle menu"
        >
          <List className="w-5 h-5" />
        </Button>
        <h2 className="text-xl font-semibold capitalize tracking-tight">
          {activeSection.replace("-", " ")}
        </h2>
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "min-h-11 min-w-11 text-muted-foreground hover:text-primary transition-colors",
            searchOpen && "bg-accent text-accent-foreground"
          )}
          aria-label="Search"
          onClick={() => setSearchOpen(!searchOpen)}
        >
          <MagnifyingGlass className="w-5 h-5" />
        </Button>

        {/* Command Palette Placeholder (Visual only for now if open) */}
        {searchOpen && (
          <div className="absolute top-16 left-0 w-full bg-background border-b p-4 shadow-md animate-in slide-in-from-top-2 z-[-1] md:hidden">
             <input 
                type="text" 
                placeholder="Search..." 
                className="w-full p-2 border rounded-md bg-muted/50"
                autoFocus
                onBlur={() => setSearchOpen(false)}
             />
          </div>
        )}

        {/* Language Selector */}
        <div className="hidden sm:block">
            <LanguageSelector
            value={preferredLanguage}
            onValueChange={handleLanguageChange}
            />
        </div>

        {/* Organization Switcher */}
        {currentUser?.organizations && currentUser.organizations.length > 1 && (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-h-9 gap-2 hidden md:flex hover:bg-accent/50 transition-colors"
              >
                <Briefcase className="w-4 h-4" />
                <span className="max-w-[120px] truncate">
                  {currentOrg?.name || "Switch Org"}
                </span>
                <CaretDown className="w-3 h-3 text-muted-foreground" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Switch Organization</DialogTitle>
                <DialogDescription>
                  Select the organization you want to work with
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 mt-2">
                {organizations
                  .filter((org) => currentUser.organizations?.includes(org.id))
                  .map((org) => (
                    <button
                      key={org.id}
                      onClick={() => handleSwitchOrganization(org.id)}
                      className={cn(
                        "w-full p-4 border rounded-lg text-left transition-all duration-200 hover:shadow-sm",
                        org.id === currentOrgId
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "hover:bg-accent/50 hover:border-accent"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-foreground">{org.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {org.slug}
                          </div>
                        </div>
                        {org.id === currentOrgId && (
                          <CheckCircle
                            className="w-5 h-5 text-primary"
                            weight="fill"
                          />
                        )}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Badge
                          variant={
                            org.status === "active" ? "default" : "secondary"
                          }
                          className="text-xs uppercase"
                        >
                          {org.status}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {org.subscriptionPlan}
                        </Badge>
                      </div>
                    </button>
                  ))}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="min-h-11 min-w-11 text-muted-foreground hover:text-primary transition-colors"
          onClick={() => setTheme(theme === "light" ? "dark" : "light")}
          aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
        >
          {theme === "light" ? (
            <Moon className="w-5 h-5" />
          ) : (
            <Sun className="w-5 h-5" />
          )}
        </Button>

        {/* Notifications */}
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative min-h-11 min-w-11 text-muted-foreground hover:text-primary transition-colors"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-destructive rounded-full ring-2 ring-background animate-pulse"></span>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Notifications</DialogTitle>
              <DialogDescription>
                You have {unreadNotifications} unread notifications
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {(notifications || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                (notifications || []).map((notif) => (
                  <div
                    key={notif.id}
                    className={cn(
                      "p-3 rounded-lg border transition-colors",
                      notif.read
                        ? "bg-background"
                        : "bg-primary/5 border-primary/20"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{notif.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {notif.message}
                        </p>
                      </div>
                      {!notif.read && (
                        <Badge variant="default" className="text-xs">
                          New
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(notif.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Profile */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 pl-2 pr-2 sm:pr-4 hover:bg-accent/50 rounded-full sm:rounded-md transition-all"
              aria-label="Open user profile"
            >
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-transparent group-hover:ring-primary/20 transition-all">
                {currentUser?.avatar && currentUser.avatar.length <= 2 ? (
                    <span className="text-sm font-medium">{currentUser.avatar}</span>
                ) : (
                    <User className="w-5 h-5 text-primary" />
                )}
              </div>
              {!isMobile && (
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium max-w-[100px] truncate">
                    {currentUser?.name || "User"}
                  </p>
                  <p className="text-xs text-muted-foreground max-w-[100px] truncate">
                    {currentUser?.role || "Employee"}
                  </p>
                </div>
              )}
              <CaretDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Profile Settings</DialogTitle>
              <DialogDescription>
                Update your profile information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl">
                        {currentUser?.avatar && currentUser.avatar.length <= 2 ? currentUser.avatar : <User className="w-8 h-8 text-primary" />}
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">{currentUser?.name || "User"}</h3>
                        <p className="text-muted-foreground text-sm">{currentUser?.email}</p>
                        <Badge variant="outline" className="mt-1">{currentUser?.role || "Employee"}</Badge>
                    </div>
                </div>

              <div className="space-y-2">
                <Button
                    onClick={handleRestartOnboarding}
                    className="w-full justify-start h-10"
                    variant="outline"
                >
                    <GraduationCap className="w-4 h-4 mr-2 text-primary" />
                    Take the Onboarding Tour
                </Button>
                <Button
                    onClick={handleKeyboardShortcuts}
                    className="w-full justify-start h-10"
                    variant="outline"
                >
                    <Keyboard className="w-4 h-4 mr-2 text-primary" />
                    View Keyboard Shortcuts
                </Button>
              </div>
              
              <div className="border-t pt-4 mt-2">
                <Button
                  variant="outline"
                  className="w-full justify-start text-destructive hover:text-destructive bg-destructive/5 hover:bg-destructive/10 border-destructive/20 hover:border-destructive/30"
                  onClick={handleLogout}
                >
                  <SignOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </header>
  );
}
